import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";

import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AssuredWorkloadIcon from "@mui/icons-material/AssuredWorkload";
import CancelIcon from "@mui/icons-material/Cancel";

import {
  showSuccessToast,
  showErrorToast,
} from "../../../common/components/Helper/Utils";

import {
  getBillingStatus,
  getBillingPlans,
  initBillingSubscription,
  cancelBillingPlan,
} from "./actions/administration";

const GOLD = "#cea05f";
const GOLD_DARK = "#a97142";

const formatPriceWithInterval = (amount, interval) => {
  if (amount === 0 || amount === "0") return "Free";
  if (amount == null || amount === "") return "—";
  return `$${amount} / ${interval}`;
};

const BillingComponent = () => {
  const [status, setStatus] = useState(null);
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const billingPopupRef = useRef(null);

  const loadBillingData = () => {
    setLoading(true);
    Promise.all([
      new Promise((resolve) => getBillingStatus((err, res) => resolve(res))),
      new Promise((resolve) => getBillingPlans((err, res) => resolve(res))),
    ]).then(([statusRes, plansRes]) => {
      if (statusRes?.success === 1) setStatus(statusRes.data);
      if (plansRes?.success === 1) setPlans(plansRes.data);
      setLoading(false);
    });
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const billing = params.get("billing");

    if (billing === "success") {
      showSuccessToast("Subscription updated successfully.");
    } else if (billing === "declined") {
      showErrorToast("Subscription change was declined.");
    } else if (billing === "error") {
      showErrorToast("Something went wrong with billing. Please try again.");
    }

    if (billing) {
      const url = new URL(window.location.href);
      url.searchParams.delete("billing");
      window.history.replaceState({}, "", url.toString());
    }

    loadBillingData();

    if ( billing &&
      (window.name === "shopifyBilling" ||
      (window.opener && !window.opener.closed))
    ) {
      try {
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage(
            { type: "SHOPIFY_BILLING_DONE", status: billing },
            "*",
          );
        }
      } catch (e) {}

      const closeTimer = setTimeout(() => {
        try {
          window.close();
        } catch (e) {}
      }, 2000);

      return () => clearTimeout(closeTimer);
    }
  }, []);

  useEffect(() => {
    const onMessage = (event) => {
      if (event?.data?.type !== "SHOPIFY_BILLING_DONE") return;
      try {
        if (billingPopupRef.current && !billingPopupRef.current.closed) {
          billingPopupRef.current.close();
        }
      } catch (e) {}
      try {
        localStorage.removeItem("shopify_billing_popup");
      } catch (e) {}
        loadBillingData();
        setSubscribing(false);
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const openShopifyBillingPopup = (url) => {
    const width = 500;
    const height = 720;
    // Open in the left corner
    const left = 16;
    const top = 16;
    const features = [
      `width=${width}`,
      `height=${height}`,
      `left=${left}`,
      `top=${top}`,
      "scrollbars=yes",
      "resizable=yes",
      "toolbar=no",
      "menubar=no",
      "location=yes",
      "status=no",
    ].join(",");

    try {
      localStorage.setItem("shopify_billing_popup", String(Date.now()));
    } catch (e) {}

    const popup = window.open(url, "shopifyBilling", features);
    billingPopupRef.current = popup;

    if (popup) {
      const timer = setInterval(() => {
        if (popup.closed) {
          clearInterval(timer);
          billingPopupRef.current = null;
          try {
            localStorage.removeItem("shopify_billing_popup");
          } catch (e) {}
          loadBillingData();
          setSubscribing(false);
        }
      }, 1000);
    } else {
      setSubscribing(false);
      showErrorToast("Popup blocked. Please allow popups and try again.");
    }
  };

  const handleSubscribe = () => {
    // Backend decides plan; send default mode for API compatibility
    const mode = plans?.defaultMode || plans?.monthly ? "monthly" : "monthly";
    setSubscribing(true);
    initBillingSubscription(mode, (err, res) => {
      if (res?.success === 1 && res.data?.confirmationUrl) {
        openShopifyBillingPopup(res.data.confirmationUrl);
        return;
      }
      if (res?.success === 1) {
        showSuccessToast("Subscription activated successfully.");
        loadBillingData();
        setSubscribing(false);
        return;
      }
      showErrorToast(res?.message || "Failed to initiate subscription. Please try again.",
      );
      setSubscribing(false);
    });
  };

  const handleCancelConfirm = () => {
    const subscriptionId = status?.subscriptionId ?? status?.subscription_id;
    if (!subscriptionId) {
      showErrorToast("Missing subscription ID. Refresh the page and try again.");
      setCancelDialogOpen(false);
      return;
    }

    setCancelLoading(true);
    cancelBillingPlan(subscriptionId, (err, res) => {
      setCancelLoading(false);
      setCancelDialogOpen(false);
      if (err) {
        showErrorToast("Failed to cancel subscription. Please try again.");
        return;
      }
      if (res?.success === 1) {
        showSuccessToast("Subscription cancelled successfully.");
        if (res.data) {
          setStatus(res.data);
        } else {
          setStatus((prev) => ({
            ...prev,
            status: "none",
            mode: null,
            subscriptionId: null,
          }));
        }
        loadBillingData();
      } else {
        showErrorToast(res?.message || "Failed to cancel subscription. Please try again.");
      }
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  const billingStatus = status?.status?.toLowerCase?.() ?? "";
  const isActive = billingStatus === "active";
  const isPending = billingStatus === "pending";
  const currentMode = status?.mode;
  const planPrice =
    status?.billingAmount ?? plans?.monthly?.price ?? plans?.annual?.price;
  const trialDays = plans?.[currentMode]?.trialDays ?? plans?.monthly?.trialDays;

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", p: 3 }}>
      <Stack direction="row" spacing={2} alignItems="center" mb={4}>
        <WorkspacePremiumIcon sx={{ color: GOLD, fontSize: "28px !important" }} />
        <Box>
          <Typography
            variant="h5"
            sx={{ fontWeight: 600, color: "#111827", fontSize: "1.35rem !important" }}
          >
            Shopify Subscription 
          </Typography>
          <Typography sx={{ color: "#6B7280 !important", mt: 0.5, fontSize: "14px !important" }}>
            Manage your WatchOps subscription and billing plan
          </Typography>
        </Box>
      </Stack>

      {/* Current status card */}
      <Card
        elevation={0}
        sx={{
          mb: 4,
          overflow: "hidden",
          borderRadius: 4,
          border: "1px solid rgba(206,160,95,.35)",
          boxShadow: "0 6px 28px rgba(206,160,95,0.18), 0 2px 8px rgba(0,0,0,0.06)",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            spacing={3}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 70,
                  height: 70,
                  borderRadius: 3,
                  background: GOLD,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <WorkspacePremiumIcon sx={{ color: "#fff", fontSize: "36px !important" }} />
              </Box>
              <Box>
                <Typography
                  sx={{ fontSize: "1.35rem !important", fontWeight: 500, color: "#111827" }}
                >
                  {status?.planName || "WatchOps"}
                </Typography>
                <Typography sx={{ color: "#6B7280 !important", fontSize: "14px !important" }}>
                  {isActive || isPending
                    ? currentMode === "annual"
                      ? "Annual billing"
                      : currentMode === "monthly"
                        ? "Monthly billing"
                        : "WatchOps billing"
                      : "No active subscription"}
                </Typography>
              </Box>
            </Stack>

              <Chip
                icon={<CheckCircleIcon style={{ color: "#ffffff" }} />}
                label={isActive ? "Active" : isPending ? "Pending Approval" : "Inactive"}
                sx={{
                  bgcolor: isActive ? "#22C55E" : isPending ? "#F59E0B" : "#6B7280", color: "#fff", fontWeight: 700 }}
              />
          </Stack>

          {(isActive || isPending) && (
            <>
              <Divider sx={{ my: 3, borderColor: "rgba(206,160,95,0.2)" }} />
              <Stack direction={{ xs: "column", md: "row" }} spacing={4}>

                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 44, height: 44, borderRadius: 2,
                      bgcolor: "rgba(206,160,95,0.1)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    <CalendarMonthIcon sx={{ color: GOLD, fontSize: "22px !important" }} />
                  </Box>
                  <Box>
                    <Typography sx={{ color: "#6B7280 !important", fontSize: "13px !important", fontWeight: 500, lineHeight: 1.2 }}>
                      Billing Mode
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: "16px !important", color: GOLD, lineHeight: 1.3 }}>
                      {currentMode === "monthly" ? "Monthly" : currentMode === "annual" ? "Annual" : "—"}
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 44, height: 44, borderRadius: 2,
                      bgcolor: "rgba(206,160,95,0.1)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    <CreditCardIcon sx={{ color: GOLD, fontSize: "22px !important" }} />
                  </Box>
                  <Box>
                    <Typography sx={{ color: "#6B7280 !important", fontSize: "13px !important", fontWeight: 500, lineHeight: 1.2 }}>
                      Plan
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: "16px !important", color: "#111827", lineHeight: 1.3 }}>
                      {status?.planName || "—"}
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 44, height: 44, borderRadius: 2,
                      bgcolor: "rgba(206,160,95,0.1)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    <AssuredWorkloadIcon sx={{ color: GOLD, fontSize: "22px !important" }} />
                  </Box>
                  <Box>
                    <Typography sx={{ color: "#6B7280 !important", fontSize: "13px !important", fontWeight: 500, lineHeight: 1.2 }}>
                      Billing Amount
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: "16px !important", color: "#111827", lineHeight: 1.3 }}>
                      {formatPriceWithInterval(
                        status?.billingAmount,
                        currentMode === "annual" ? "yr" : "mo"
                      )}
                    </Typography>
                  </Box>
                </Stack>

              </Stack>
            </>
          )}
        </CardContent>
      </Card>

      {/* Single subscribe / active section — no monthly/annual switch */}
      {!isActive && !isPending ? (
        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            border: "1px solid #E5E7EB",
            maxWidth: "100%",
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "17px !important",
                color: "#111827",
                mb: 1,
              }}
            >
              Activate WatchOps Billing
                </Typography>
                <Typography
sx={{
                color: "#6B7280 !important",
                fontSize: "14px !important",
                mb: 2,
              }}
            >
              Subscribe to unlock all WatchOps capabilities. You will confirm
              the charge in Shopify.
                </Typography>

            {(planPrice === 0 || planPrice === "0") && (
              <Typography
                sx={{
                  color: "#22C55E !important",
                  fontSize: "13px !important",
                  fontWeight: 600,
                  mb: 2,
                }}
              >
                Current plan: Free
              </Typography>
            )}

            {trialDays > 0 && (
                <Typography sx={{ color: "#22C55E !important", fontSize: "13px !important",
              fontWeight: 600,
              mb: 2,
                }}
              >
                {trialDays}-day free trial included
              </Typography>
            )}

              <Button
                variant="contained"
                size="medium"
               startIcon={
                subscribing ? (
                  <CircularProgress size={16} sx={{ color: "#fff" }} />
                ) : (
                  <WorkspacePremiumIcon />
                )
              }
              disabled={subscribing}
              onClick={handleSubscribe}
                sx={{
                  textTransform: "none",
                  borderRadius: "10px",
                px: 3,
                py: 1,
               minWidth: 140,
                  backgroundColor: GOLD,
                  fontWeight: 700,
                  "&:hover": { backgroundColor: GOLD_DARK },
                }}
              >
              {subscribing ? "Redirecting…" : "Subscribe"}
              </Button>
          </CardContent>
        </Card>
        ) : (
        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            border: "1px solid #A5D6A7",
            background: "#E8F5E9",
            maxWidth: "100%",
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "16px !important",
                color: "#2E7D32",
                mb: 0.5,
              }}
            >
              {isPending
                ? "Billing approval is pending"
                : "Your WatchOps billing is active"}
            </Typography>
            <Typography
              sx={{ color: "#388E3C !important", fontSize: "14px !important" }}
            >
              {isPending
                ? "Complete the charge confirmation in Shopify if the popup is still open."
                : "No further action needed. Your subscription is active."}
            </Typography>
          </CardContent>
        </Card>     )}

      <Dialog
        open={cancelDialogOpen}
        onClose={() => !cancelLoading && setCancelDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#111827" }}>
          Cancel Subscription?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "#6B7280" }}>
            Your subscription will be cancelled immediately on Shopify. You will
            lose access to all premium features. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setCancelDialogOpen(false)}
            disabled={cancelLoading}
            sx={{ textTransform: "none", color: "#6B7280" }}
          >
            Keep Subscription
          </Button>
          <Button
            onClick={handleCancelConfirm}
            disabled={cancelLoading}
            variant="contained"
            startIcon={cancelLoading ? (<CircularProgress size={16} sx={{ color: "#fff" }} />) : (<CancelIcon />)}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              bgcolor: "#EF4444",
              fontWeight: 700,
              "&:hover": { bgcolor: "#DC2626" },
            }}
          >
            {cancelLoading ? "Cancelling…" : "Yes, Cancel"}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default BillingComponent;
